import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  pushSupported, currentPermission,
  ensurePushPermissionAndSubscribe, disablePush,
} from '../utils/pushManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable "Enable match push alerts" CTA. Drops into buyer + investor
 * dashboards. Self-contained — owns its own state.
 */
export const EnableMatchPushButton = ({ variant = 'inline' }) => {
  const [supported, setSupported] = useState(() => pushSupported());
  const [perm, setPerm] = useState(() => currentPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return undefined;
    let cancelled = false;
    const probe = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        const sub = reg && (await reg.pushManager.getSubscription());
        if (!cancelled) setSubscribed(!!sub);
      } catch (_e) { /* ignore */ }
    };
    probe();
    return () => { cancelled = true; };
  }, [supported]);

  if (!supported) {
    return (
      <div
        data-testid="push-unsupported"
        className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500"
      >
        Your browser doesn&apos;t support push notifications. Try Chrome, Edge, or Firefox.
      </div>
    );
  }

  const enable = async () => {
    setBusy(true);
    try {
      await ensurePushPermissionAndSubscribe();
      setPerm(currentPermission());
      setSubscribed(true);
      toast.success('Push alerts enabled. Sending a test ping…');
      try { await axios.post(`${API}/push/test`); } catch (_e) { /* ignore — non-blocking */ }
    } catch (e) {
      toast.error(e.message || 'Could not enable push.');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setSubscribed(false);
      toast.success('Push alerts turned off.');
    } catch (e) {
      toast.error(e.message || 'Could not disable push.');
    } finally {
      setBusy(false);
    }
  };

  const isCompact = variant === 'compact';

  if (subscribed) {
    return (
      <button
        type="button"
        onClick={disable}
        disabled={busy}
        data-testid="push-disable-btn"
        className={`inline-flex items-center gap-2 ${
          isCompact ? 'text-xs text-slate-500' : 'text-sm text-slate-600'
        } hover:text-slate-800 transition-colors`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />}
        Push alerts on · turn off
      </button>
    );
  }

  if (perm === 'denied') {
    return (
      <div
        data-testid="push-denied"
        className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
      >
        Push alerts are blocked in your browser settings. Unblock and refresh to enable.
      </div>
    );
  }

  return (
    <Button
      onClick={enable}
      disabled={busy}
      data-testid="push-enable-btn"
      className={`bg-[#0F172A] hover:bg-[#1F2937] text-white ${
        isCompact ? 'h-9 px-3 text-xs' : 'py-5 font-semibold'
      }`}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
      ) : (
        <Bell className={`${isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} mr-2`} />
      )}
      Get instant match alerts
    </Button>
  );
};

export default EnableMatchPushButton;
